import React, { useMemo, useRef, useEffect, Suspense, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Float } from '@react-three/drei';
import * as THREE from 'three';
import { OBJLoader, MeshSurfaceSampler } from 'three-stdlib';
import { generateServiceShape } from '../utils/particleShapes';
import phoneObjPath from '../assets/objects/app.obj?url';
import '../styles/service-visualization.css';

const PRIMARY_COLOR = '#77F76C';
const PARTICLE_COUNT = 2000;

interface ServiceVisualizationProps {
  serviceType: string;
  isTransitioning?: boolean;
  className?: string;
}

interface Waypoint {
  position: THREE.Vector3;
  neighbors: number[];
}

interface Particle {
  currentWaypoint: number;
  targetWaypoint: number;
  progress: number;
  speed: number;
  dx: number;
  dy: number;
  dz: number;
  transitionStart?: THREE.Vector3;
  isTransitioning?: boolean;
  transitionTime?: number;
}

const Particles = ({ serviceType }: { serviceType: string }) => {
  const pointsRef = useRef<THREE.Points>(null);
  const geometryRef = useRef<THREE.BufferGeometry>(null);
  
  // State for the graph system
  const [systemData, setSystemData] = useState<{
    waypoints: Waypoint[];
    particles: Particle[];
    positions: Float32Array;
    sizes: Float32Array;
  } | null>(null);

  // Initialize system when serviceType changes
  useEffect(() => {
    let isActive = true;

    const initSystem = async () => {
      // 1. Generate Waypoints
      let generatedWaypoints: THREE.Vector3[] = [];
      
      if (serviceType === 'mobile-app') {
        try {
          const loader = new OBJLoader();
          const obj = await new Promise<THREE.Group>((resolve, reject) => {
            loader.load(phoneObjPath, resolve, undefined, reject);
          });

          let mesh: THREE.Mesh | null = null;
          obj.traverse((child) => {
            if ((child as THREE.Mesh).isMesh && !mesh) {
              mesh = child as THREE.Mesh;
            }
          });

          if (mesh) {
            const sampler = new MeshSurfaceSampler(mesh).build();
            const tempPos = new THREE.Vector3();
            
            // Center and scale logic
            const box = new THREE.Box3().setFromObject(mesh);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 4.0 / maxDim;

            // Sample waypoints (fewer than particles usually, but here we can match or use less)
            // HeroParticles uses 1500 waypoints for 2000 particles
            const WAYPOINT_COUNT = 1500;
            
            for (let i = 0; i < WAYPOINT_COUNT; i++) {
              sampler.sample(tempPos);
              generatedWaypoints.push(new THREE.Vector3(
                (tempPos.x - center.x) * scale,
                (tempPos.y - center.y) * scale,
                (tempPos.z - center.z) * scale
              ));
            }
          }
        } catch (e) {
          console.error("Error loading OBJ:", e);
          // Fallback to sphere if loading fails
          const rawPoints = generateServiceShape('server-software', 1500);
          for (let i = 0; i < rawPoints.length; i += 3) {
            generatedWaypoints.push(new THREE.Vector3(rawPoints[i], rawPoints[i+1], rawPoints[i+2]));
          }
        }
      } else {
        // Use generateServiceShape
        // We generate waypoints, not particles directly
        const WAYPOINT_COUNT = 1500;
        const rawPoints = generateServiceShape(serviceType, WAYPOINT_COUNT);
        for (let i = 0; i < rawPoints.length; i += 3) {
          generatedWaypoints.push(new THREE.Vector3(rawPoints[i], rawPoints[i+1], rawPoints[i+2]));
        }
      }

      if (!isActive) return;
      if (generatedWaypoints.length === 0) return;

      // 2. Build Graph (Connect nearest neighbors)
      const waypointsStruct: Waypoint[] = generatedWaypoints.map(p => ({
        position: p,
        neighbors: []
      }));

      const K_NEIGHBORS = 8;
      
      // Simple O(N^2) neighbor search - acceptable for N=1500
      waypointsStruct.forEach((wp, i) => {
        const distances: { idx: number, dist: number }[] = [];
        for (let j = 0; j < waypointsStruct.length; j++) {
          if (i === j) continue;
          const d = wp.position.distanceToSquared(waypointsStruct[j].position);
          distances.push({ idx: j, dist: d });
        }
        distances.sort((a, b) => a.dist - b.dist);
        wp.neighbors = distances.slice(0, K_NEIGHBORS).map(d => d.idx);
      });

      // Capture old positions for transition
      const oldPositions = geometryRef.current && geometryRef.current.attributes.position 
        ? new Float32Array(geometryRef.current.attributes.position.array) 
        : null;

      // 3. Initialize Particles
      const parts: Particle[] = [];
      const pos = new Float32Array(PARTICLE_COUNT * 3);
      const sizes = new Float32Array(PARTICLE_COUNT);

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const startIdx = Math.floor(Math.random() * waypointsStruct.length);
        const startWp = waypointsStruct[startIdx];
        const targetIdx = startWp.neighbors.length > 0 
          ? startWp.neighbors[Math.floor(Math.random() * startWp.neighbors.length)]
          : startIdx;
        
        parts.push({
          currentWaypoint: startIdx,
          targetWaypoint: targetIdx,
          progress: Math.random(),
          speed: 0.001 + Math.random() * 0.002,
          dx: 0,
          dy: 0,
          dz: 0,
          transitionStart: oldPositions ? new THREE.Vector3(
            oldPositions[i*3],
            oldPositions[i*3+1],
            oldPositions[i*3+2]
          ) : undefined,
          isTransitioning: !!oldPositions,
          transitionTime: 0
        });

        const p1 = waypointsStruct[startIdx].position;
        const p2 = waypointsStruct[targetIdx].position;
        const currentPos = new THREE.Vector3().lerpVectors(p1, p2, parts[i].progress);
        
        pos[i*3] = currentPos.x;
        pos[i*3+1] = currentPos.y;
        pos[i*3+2] = currentPos.z;

        // Random size between 0.5 and 1.5 of base size
        sizes[i] = 0.5 + Math.random() * 1.0;
      }

      setSystemData({
        waypoints: waypointsStruct,
        particles: parts,
        positions: pos,
        sizes: sizes
      });
    };

    initSystem();

    return () => { isActive = false; };
  }, [serviceType]);

  // Custom shader for particles (Copied from HeroParticles)
  const shaderArgs = useMemo(
    () => ({
      uniforms: {
        uColor: { value: new THREE.Color(PRIMARY_COLOR) },
        uBaseSize: { value: 0.15 },
        uGlowStrength: { value: 3.0 },
      },
      vertexShader: `
        uniform float uBaseSize;
        attribute float aSize;
        
        void main() {
          vec3 pos = position;
          vec4 mvPosition = viewMatrix * modelMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = uBaseSize * aSize * (300.0 / -mvPosition.z);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uGlowStrength;
        
        void main() {
          vec2 center = gl_PointCoord - 0.5;
          float dist = length(center);
          if (dist > 0.5) discard;
          
          // Create a soft glow effect
          float strength = 1.0 - (dist * 2.0);
          strength = pow(strength, 1.5);
          strength *= uGlowStrength;
          
          gl_FragColor = vec4(uColor, strength);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    }),
    []
  );

  const currentScale = useRef(0);
  
  // Temp vectors for mouse interaction to avoid GC
  const tempVec = useMemo(() => new THREE.Vector3(), []);
  const tempClosest = useMemo(() => new THREE.Vector3(), []);
  const tempDir = useMemo(() => new THREE.Vector3(), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const inverseMatrix = useMemo(() => new THREE.Matrix4(), []);

  useFrame((state, delta) => {
    if (!systemData || !pointsRef.current || !geometryRef.current) return;
    
    const { particles, waypoints } = systemData;

    // Rotate horizontally (Y axis)
    pointsRef.current.rotation.y += 0.001;
    pointsRef.current.updateMatrixWorld();

    // Popup animation
    currentScale.current = THREE.MathUtils.lerp(currentScale.current, 1, delta * 3);
    pointsRef.current.scale.setScalar(currentScale.current);

    // Mouse Interaction Setup
    raycaster.setFromCamera(state.pointer, state.camera);
    const ray = raycaster.ray;
    
    // Transform ray to local space
    inverseMatrix.copy(pointsRef.current.matrixWorld).invert();
    ray.applyMatrix4(inverseMatrix);

    const REPULSION_RADIUS = 0.5;
    const REPULSION_STRENGTH = 0.1; // Significantly reduced strength (approx 1/6 of 0.8)
    const radiusSq = REPULSION_RADIUS * REPULSION_RADIUS;

    const posAttr = geometryRef.current.attributes.position;
    const array = posAttr.array as Float32Array;

    for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.progress += p.speed;

        if (p.progress >= 1.0) {
            p.progress = 0;
            p.currentWaypoint = p.targetWaypoint;
            const currentWp = waypoints[p.currentWaypoint];
            if (currentWp.neighbors.length > 0) {
                p.targetWaypoint = currentWp.neighbors[Math.floor(Math.random() * currentWp.neighbors.length)];
            }
        }

        const p1 = waypoints[p.currentWaypoint].position;
        const p2 = waypoints[p.targetWaypoint].position;

        // Lerp for network movement
        let baseX = p1.x + (p2.x - p1.x) * p.progress;
        let baseY = p1.y + (p2.y - p1.y) * p.progress;
        let baseZ = p1.z + (p2.z - p1.z) * p.progress;

        // Transition logic
        if (p.isTransitioning && p.transitionStart) {
            p.transitionTime = (p.transitionTime || 0) + delta * 1.5;
            if (p.transitionTime >= 1.0) {
                p.isTransitioning = false;
                p.transitionStart = undefined;
            } else {
                const t = 1 - Math.pow(1 - p.transitionTime, 3); // Ease out cubic
                baseX = p.transitionStart.x + (baseX - p.transitionStart.x) * t;
                baseY = p.transitionStart.y + (baseY - p.transitionStart.y) * t;
                baseZ = p.transitionStart.z + (baseZ - p.transitionStart.z) * t;
            }
        }

        // Mouse Repulsion Calculation
        tempVec.set(baseX, baseY, baseZ);
        const distSq = ray.distanceSqToPoint(tempVec);

        let targetDx = 0;
        let targetDy = 0;
        let targetDz = 0;

        if (distSq < radiusSq) {
            ray.closestPointToPoint(tempVec, tempClosest);
            const dist = Math.sqrt(distSq);
            const force = (REPULSION_RADIUS - dist) / REPULSION_RADIUS; // 0 to 1
            
            // Direction from closest point on ray to particle
            tempDir.copy(tempVec).sub(tempClosest).normalize();
            
            // Calculate target displacement
            targetDx = tempDir.x * force * REPULSION_STRENGTH;
            targetDy = tempDir.y * force * REPULSION_STRENGTH;
            targetDz = tempDir.z * force * REPULSION_STRENGTH;
        }

        // Smoothly interpolate current displacement towards target displacement
        // Factor 0.1 gives a nice smooth transition
        p.dx += (targetDx - p.dx) * 0.1;
        p.dy += (targetDy - p.dy) * 0.1;
        p.dz += (targetDz - p.dz) * 0.1;

        // Apply final position
        array[i*3] = baseX + p.dx;
        array[i*3+1] = baseY + p.dy;
        array[i*3+2] = baseZ + p.dz;
    }

    posAttr.needsUpdate = true;
  });

  if (!systemData) return null;

  return (
    <points ref={pointsRef}>
      <bufferGeometry ref={geometryRef}>
        <bufferAttribute
          attach="attributes-position"
          count={systemData.particles.length}
          array={systemData.positions}
          itemSize={3}
          args={[systemData.positions, 3]}
        />
        <bufferAttribute
          attach="attributes-aSize"
          count={systemData.particles.length}
          array={systemData.sizes}
          itemSize={1}
          args={[systemData.sizes, 1]}
        />
      </bufferGeometry>
      <shaderMaterial args={[shaderArgs]} />
    </points>
  );
};

const ServiceVisualization: React.FC<ServiceVisualizationProps> = ({ serviceType, className }) => {
  return (
    <div className={`service-visualization ${className || ''}`} style={{ background: 'transparent' }}>
      <div className="visualization-container" style={{ width: '100%', height: '100%' }}>
        <Canvas 
          camera={{ position: [0, 0, 6], fov: 45 }} 
          dpr={[1, 2]}
          style={{ pointerEvents: 'none' }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
              <Particles serviceType={serviceType} />
            </Float>
            <OrbitControls 
              enableZoom={false} 
              enablePan={false} 
              enableRotate={false}
              autoRotate={true}
              autoRotateSpeed={0.5}
              minPolarAngle={Math.PI / 4}
              maxPolarAngle={Math.PI * 3 / 4}
            />
          </Suspense>
        </Canvas>
        {/* CSS Blur overlay matching HeroParticles */}
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            maxWidth: '600px',
            maxHeight: '600px',
            background: PRIMARY_COLOR,
            filter: 'blur(100px)',
            opacity: 0.08,
            pointerEvents: 'none',
            zIndex: -1
        }} />
      </div>
    </div>
  );
};

export default ServiceVisualization;
